import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, BookOpen, Leaf, Zap, Users, Download, Quote, Loader2 } from "lucide-react";

const KIND_ICON = { journal: BookOpen, practice: Leaf, breakout: Zap, booking: Users };
const KIND_KEY = { journal: "sm_kind_journal", practice: "sm_kind_practice", breakout: "sm_kind_breakout", booking: "sm_kind_booking" };
const LOCALE_TAG = { de: "de-DE", en: "en-US", es: "es-ES", fr: "fr-FR" };

export default function StoryMode() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("sm_title"));
  const tag = LOCALE_TAG[lang] || "en-US";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/story-mode");
      setData(d);
    } catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const quoteByChapter = useMemo(() => {
    const out = {};
    if (!data?.chapters?.length) return out;
    const step = Math.max(1, Math.ceil(data.chapters.length / (data.quotes?.length || 1)));
    (data.quotes || []).forEach((q, i) => { const target = i * step; if (target < data.chapters.length) out[target] = q; });
    return out;
  }, [data]);

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString(tag, { weekday: "long" })} · ${d.toLocaleDateString(tag, { month: "short", day: "numeric" })}`;
    } catch { return (iso || "").slice(0, 10); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/story-mode/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "story_mode.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("something_went_wrong")); } finally { setDownloading(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const empty = !data || !data.chapters?.length;
  const meta = data?.meta || {};

  return (
    <div className="mx-auto max-w-2xl" data-testid="story-mode">
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="sm-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-xl text-foreground">{t("sm_title")}</h1>
      </div>

      <div className="mb-8 border-b border-border pb-6">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-primary">{t("sm_cover_eyebrow")}</p>
        <h2 className="mt-2 font-display text-3xl leading-tight text-foreground">{t("sm_cover_title")}</h2>
        {!empty && meta.total_entries > 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">{t("sm_cover_meta", { entries: meta.total_entries, days: meta.span_days, chapters: data.chapters.length })}</p>
        ) : null}
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{t("sm_cover_body")}</p>
      </div>

      {empty ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <BookOpen className="h-10 w-10 text-primary" />
          <p className="font-display text-lg text-foreground">{t("sm_empty_title")}</p>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">{t("sm_empty_body")}</p>
        </div>
      ) : (
        <>
          {data.chapters.map((ch, idx) => (
            <div key={ch.month} className="mb-10">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t("sm_chapter", { n: idx + 1 })}</p>
              <h3 className="mt-1 font-display text-2xl text-primary">{ch.month_label}</h3>
              <p className="mt-1.5 text-sm italic leading-relaxed text-muted-foreground">{ch.caption}</p>

              <div className="mt-6 space-y-5">
                {ch.entries.map((e, i) => {
                  const Icon = KIND_ICON[e.kind] || BookOpen;
                  return (
                    <div key={`${e.at_iso}-${i}`} className="border-b border-border pb-4">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                        <Icon className="h-4 w-4 text-primary" />
                        <span>{fmt(e.at_iso)} · {t(KIND_KEY[e.kind] || "sm_kind_journal")}</span>
                      </div>
                      <p className="mt-1.5 font-semibold text-foreground">{e.title}</p>
                      {e.body ? <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">{e.body}</p> : null}
                      {e.kind === "practice" && e.mood_before != null && e.mood_after != null ? (
                        <p className="mt-1.5 text-xs text-muted-foreground">{t("sm_mood", { a: e.mood_before, b: e.mood_after })}</p>
                      ) : null}
                      {e.kind === "breakout" && e.intensity_before != null && e.intensity_after != null ? (
                        <p className="mt-1.5 text-xs text-muted-foreground">{t("sm_intensity", { a: e.intensity_before, b: e.intensity_after })}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {quoteByChapter[idx] ? (
                <div className="mt-4 flex gap-3 rounded-r-lg border-l-4 border-primary bg-secondary/60 p-5">
                  <Quote className="h-4 w-4 shrink-0 text-primary" />
                  <p className="font-display text-lg italic leading-relaxed text-primary">"{quoteByChapter[idx].text}."</p>
                </div>
              ) : null}
            </div>
          ))}

          <div className="flex flex-col items-center gap-3 pt-2">
            <button onClick={downloadPdf} disabled={downloading} data-testid="sm-download-pdf" className="flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground disabled:opacity-60">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t("sm_download_pdf")}
            </button>
            <p className="max-w-xs text-center text-xs leading-relaxed text-muted-foreground">{t("sm_footer_note")}</p>
          </div>
        </>
      )}
    </div>
  );
}
